import type { AvailableTransition } from '../state/api.js';

/**
 * Keputusan bilah aksi layar Detail Temuan — UAT D-04.
 *
 * Konsol dulu mematikan setiap transisi yang punya `blocked_by`. Itu benar untuk
 * satu jenis penghalang dan salah untuk jenis lainnya, dan keduanya dulu dikirim
 * lewat medan yang sama:
 *
 *   · "foto sesudah belum ada"  → syarat KEADAAN. Tombol memang harus mati, dan
 *     ia mengatakan sebabnya. Ini yang membuat "Terlampir 0 dari 1" mungkin.
 *   · "penanggung jawabnya belum dipilih" → syarat MASUKAN. Tombol harus HIDUP,
 *     karena menekannya justru cara pengguna memberikan masukan itu.
 *
 * Server sekarang memisahkannya (`blocked_by` vs `requires_input`, kernel A
 * `partitionGuards`). Berkas ini menerjemahkan pemisahan itu menjadi keputusan
 * layar, sebagai fungsi murni — supaya ia dapat diuji tanpa peramban dan tidak
 * dapat bergeser diam-diam di dalam JSX.
 */

export type ActionFieldKind = 'owner' | 'date' | 'reason' | 'text';

export interface ActionInputField {
  /** Nama medan seperti yang dikirim di `TransitionRequest.input`. */
  name: string;
  kind: ActionFieldKind;
  /** Kunci i18n label medannya. */
  labelKey: string;
  required: boolean;
}

export interface ActionState {
  disabled: boolean;
  /** Kunci i18n kalimat "mengapa tombol ini mati", atau null. */
  blockedReasonKey: string | null;
  /** Benar bila menekan tombolnya membuka dialog untuk mengumpulkan masukan. */
  needsDialog: boolean;
  fields: ActionInputField[];
  /** `role="alertdialog"` dan tombol utama merah (peta §5.9). */
  destructive: boolean;
}

/**
 * Medan yang BUKAN urusan dialog bilah aksi.
 *
 * Guard `CREATE` menyebut `input.attachments`, `input.location_id`, dan
 * `input.category_code`; ketiganya dikumpulkan layar Lapor Temuan di aplikasi
 * lapangan, bukan dialog satu-tombol di konsol. `CREATE` sendiri tidak pernah
 * muncul di bilah aksi (`from: []`), tetapi daftar ini menjaga agar transisi lain
 * yang menyebut lampiran tidak menumbuhkan medan unggah di dalam dialog.
 */
const BUKAN_MEDAN_DIALOG = new Set(['attachments', 'item']);

const KIND_PER_MEDAN: Record<string, { kind: ActionFieldKind; labelKey: string }> = {
  owner_id: { kind: 'owner', labelKey: 'finding.owner' },
  due_at: { kind: 'date', labelKey: 'finding.due' },
  reason_code: { kind: 'reason', labelKey: 'finding.reason' },
  location_id: { kind: 'text', labelKey: 'finding.area' },
  category_code: { kind: 'text', labelKey: 'finding.category' },
  description: { kind: 'text', labelKey: 'finding.description' },
};

/** Transisi yang, bila dibatalkan, menghapus pekerjaan orang lain. */
const MERUSAK = new Set(['SEND_BACK', 'REOPEN', 'DE_ESCALATE']);

/**
 * Kode alasan baku per transisi.
 *
 * Guard `SEND_BACK` menuntut `input.reason_code` — sebuah KODE, bukan prosa —
 * sementara kalimat yang akan dibaca orang lain masuk `reason`. Layar tidak
 * meminta pengguna mengarang kode; ia mengirim kode yang sudah melekat pada
 * aksinya dan meminta kalimatnya.
 */
const REASON_CODE_BAKU: Record<string, string> = {
  SEND_BACK: 'SENT_BACK_BY_VERIFIER',
  REOPEN: 'REOPENED_BY_MANAGER',
  CHANGE_DUE_DATE: 'DUE_DATE_CHANGED',
  REQUEST_INFO: 'INFORMATION_REQUESTED',
  DE_ESCALATE: 'DE_ESCALATED_BY_MANAGER',
};

export function actionState(transition: AvailableTransition): ActionState {
  const disabled = transition.blocked_by !== null;

  const fields: ActionInputField[] = [];
  for (const name of transition.requires_input ?? []) {
    if (BUKAN_MEDAN_DIALOG.has(name)) continue;
    // `reason_code` dipenuhi kode baku di atas; yang diminta pengguna adalah
    // KALIMATNYA, dan kalimat itu medan `reason`.
    if (name === 'reason_code') continue;
    /**
     * Medan yang belum dikenal konsol tetap DITAWARKAN, dengan label umum.
     *
     * Label-nya `field.unknown`, bukan `field.${name}`: sebuah kunci yang dirakit
     * dari nama medan adalah kunci yang PASTI hilang dari katalog begitu sebuah
     * workflow menyebut medan baru — yaitu D-06 yang dibangun ulang dengan tangan.
     * Satu kunci umum dalam dua bahasa lebih jujur daripada nama medan mentah.
     */
    const spec = KIND_PER_MEDAN[name] ?? { kind: 'text' as ActionFieldKind, labelKey: 'field.unknown' };
    fields.push({ name, kind: spec.kind, labelKey: spec.labelKey, required: true });
  }

  const mintaAlasan =
    transition.reason_required || (transition.requires_input ?? []).includes('reason_code');
  if (mintaAlasan) {
    fields.push({ name: 'reason', kind: 'reason', labelKey: 'finding.reason', required: true });
  }

  return {
    disabled,
    blockedReasonKey: transition.blocked_by ? `error.${transition.blocked_by}` : null,
    needsDialog: !disabled && fields.length > 0,
    fields,
    destructive: MERUSAK.has(transition.code),
  };
}

export type ActionValues = Record<string, string>;

export interface TransitionRequestBody {
  transition: string;
  reason: string | null;
  input?: Record<string, unknown>;
}

/**
 * Mengubah nilai medan `<input>` menjadi badan `POST …/transitions`.
 *
 * `<input type="date">` memberi `YYYY-MM-DD`, sementara guard `CHANGE_DUE_DATE`
 * membandingkannya dengan `now`. Sebuah tanggal tanpa jam menjadi tengah malam
 * UTC, dan tenggat "hari ini" karena itu akan selalu ditolak DUE_DATE_IN_PAST
 * beberapa jam sebelum hari itu berakhir. Tenggat berarti AKHIR hari yang dipilih.
 */
export function buildTransitionRequest(
  transition: AvailableTransition,
  values: ActionValues,
): TransitionRequestBody {
  const input: Record<string, unknown> = {};

  for (const name of transition.requires_input ?? []) {
    if (BUKAN_MEDAN_DIALOG.has(name) || name === 'reason_code') continue;
    const raw = values[name];
    if (raw === undefined || raw === '') continue;
    input[name] = name === 'due_at' ? akhirHariIso(raw) : raw;
  }

  if ((transition.requires_input ?? []).includes('reason_code')) {
    input.reason_code = REASON_CODE_BAKU[transition.code] ?? 'OTHER';
  }

  const reason = (values.reason ?? '').trim();
  const body: TransitionRequestBody = {
    transition: transition.code,
    reason: reason === '' ? null : reason,
  };
  if (Object.keys(input).length > 0) body.input = input;
  return body;
}

/** `2026-12-31` → `2026-12-31T23:59:59.000Z`. Nilai yang sudah ISO dibiarkan. */
export function akhirHariIso(nilai: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(nilai)) return `${nilai}T23:59:59.000Z`;
  return nilai;
}

/** Tombol Simpan pada dialog mati sampai seluruh medan wajibnya terisi. */
export function isComplete(fields: readonly ActionInputField[], values: ActionValues): boolean {
  return fields.every((f) => !f.required || (values[f.name] ?? '').trim() !== '');
}
